import { Injectable } from '@angular/core';

import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concatMap, filter, map } from 'rxjs';

import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { isTeaserSlipId } from '../../bet-generation/services/slip.utils';
import { BetslipTypesActions } from '../../types/actions';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipTeaserActions } from '../actions';
import { teaserBetStateSelector } from '../selectors';

@Injectable()
export class TeaserBetStakeEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private userSettings: SyncUserSettingsService,
    ) {}

    setStake$ = createEffect(() => {
        const _this = this;

        return this.actions$.pipe(
            ofType(BetslipTypesActions.setSelectedType),
            concatLatestFrom(() => this.store.select(teaserBetStateSelector)),
            filter(([action, _]) => action.betslipType === BetslipType.Teaser),
            concatMap(function* ([_, state]) {
                const defaultStake = _this.userSettings.defaultStake;

                if (!state.stake) {
                    yield BetslipTeaserActions.setTeaserBetStake({
                        stake: defaultStake,
                        stakeStr: defaultStake?.toString() || null,
                    });
                }
            }),
        );
    });

    setTeaserBetStake$ = createEffect(() => {
        const _this = this;

        return this.actions$.pipe(
            ofType(BetslipTypesActions.setStake),
            concatLatestFrom(() => this.store.select(betslipCurrentTypeSelector)),
            filter(([_, currentType]) => currentType === BetslipType.Teaser),
            map(([action, _]) => {
                const defaultStake = _this.userSettings.defaultStake;

                return BetslipTeaserActions.setTeaserBetStake({
                    stake: action.stake,
                    stakeStr: defaultStake?.toString() || null,
                });
            }),
        );
    });

    /**
     * When add new pick check if this pick has no stake set.
     */

    setDefaultStake$ = createEffect(() => {
        const _this = this;

        return this.actions$.pipe(
            ofType(BetslipTeaserActions.addUpdatePick),
            concatLatestFrom(() => this.store.select(teaserBetStateSelector)),
            filter(([_, state]) => !state.stake),
            concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
            map(([action, isLinear]) => {
                const defaultStake = isLinear ? null : _this.userSettings.defaultStake;

                return BetslipTeaserActions.setTeaserBetStake({
                    stake: defaultStake,
                    stakeStr: defaultStake?.toString() || null,
                });
            }),
        );
    });

    setDefaultStakeWhenNoPicks$ = createEffect(() => {
        const _this = this;

        return this.actions$.pipe(
            ofType(BetslipTeaserActions.removePick, BetslipTeaserActions.removePicks),
            concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
            concatLatestFrom(() => this.store.select(teaserBetStateSelector)),
            concatMap(function* ([[_, isLinear], state]) {
                const teaserPicksCount = Object.keys(state.picks).length;
                const defaultStake = isLinear ? null : _this.userSettings.defaultStake;
                if (teaserPicksCount < 2) {
                    yield BetslipTeaserActions.setTeaserBetStake({
                        stake: defaultStake,
                        stakeStr: defaultStake?.toString() || null,
                    });
                }
            }),
        );
    });

    setTeaserBetStakeFromStakeHint$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.setStakeFromSlipStakeHint),
            filter((stakeHint) => isTeaserSlipId(stakeHint.slipStakeHint.slipId)),
            map(({ slipStakeHint }) => {
                return BetslipTeaserActions.setTeaserBetStake({ stake: slipStakeHint.newStakeHint, stakeStr: null });
            }),
        );
    });
}
